<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePreferredReducedMotion } from '@vueuse/core';
import { format } from 'date-fns';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DebtConversationApi from 'dashboard/api/debtConversation';
import { useDateFnsLocale } from 'shared/composables/useDateFnsLocale';

// DOSSIE do painel de detalhe do Kanban: duas abas (DEBTHUB / Pipedrive) para UM candidato
// ou para o vínculo confirmado — nunca os dois ao mesmo tempo, e nunca um terceiro estado
// (ver `kind`).
//
// ABA DEBTHUB — SEM CONSULTA PRÓPRIA. `subject` é o mesmo objeto que
// Debt::DebthubLinkResolver#payload já devolveu na abertura da conversa
// (KanbanDebthubLinkPanel monta isso via useDebthubLink), agora incluindo `subject.dossie`
// (Debt::DebthubPhoneLookupService#dossie: telefones, emails, endereços, pastas e acordos —
// o dado que a mesma consulta já trazia e que o serviço só passou a repassar em 2026-08-18).
// Abrir esta aba não soma nenhuma chamada de rede.
//
// ABA PIPEDRIVE — TEM consulta própria, mas só quando o agente ABRE a aba (nunca ao abrir o
// modal nem o painel). `dealId`/`personId` (derivados de `subject`, ver abaixo) disparam
// Debt::PipedriveLookupService via Api::V2::Accounts::Debt::DebthubLinksController#pipedrive
// — cache e teto de cota vivem inteiramente do lado do servidor; este componente só evita
// REPETIR a mesma chamada enquanto o painel permanece aberto sobre o mesmo id
// (`fetchedPipedriveKey`). O que o DEBTHUB já sabia (status/erro de sincronização, ids,
// links) continua sendo a camada BASE, sempre visível sem depender da rede — a chamada ao
// vivo só ACRESCENTA (título, valor, atividades, dono) ou, se falhar, deixa a base de pé com
// um aviso. Nunca tela quebrada, nunca spinner infinito (timeout do lado do servidor).
const props = defineProps({
  subject: { type: Object, required: true },
  // 'proposed': candidato ainda em avaliação — mostra similaridade e
  // motivo. 'confirmed': vínculo já decidido — mostra quem confirmou e
  // quando. Não existe terceiro valor.
  kind: {
    type: String,
    required: true,
    validator: value => ['proposed', 'confirmed'].includes(value),
  },
  // display_id da conversa — só usado para a chamada AO VIVO do Pipedrive (ver acima). Nulo
  // quando o chamador não o fornece (ex.: os specs que exercitam só a aba DEBTHUB): a aba
  // Pipedrive então mostra apenas a camada base, sem tentar nenhuma rede.
  conversationDisplayId: { type: [String, Number], default: null },
});

const emit = defineEmits(['close']);

const { t, te } = useI18n();
const { dateFnsLocale } = useDateFnsLocale();

const close = () => emit('close');

// candidate (pendente) e link (confirmado) nomeiam os mesmos campos de forma
// diferente (ver Debt::DebthubLinkResolver#candidate_payload e #link_payload)
// — divergência de convenção do payload, não do dado. `??` unifica os dois
// formatos aqui para o template não bifurcar por `kind` campo a campo.
const personId = computed(
  () => props.subject.person_id ?? props.subject.pipedrive_person_id ?? null
);
const personUrl = computed(
  () => props.subject.person_url ?? props.subject.pipedrive_person_url ?? null
);
const dealId = computed(
  () => props.subject.deal_id ?? props.subject.pipedrive_deal_id ?? null
);
const dealUrl = computed(
  () => props.subject.deal_url ?? props.subject.pipedrive_deal_url ?? null
);
const dealStatus = computed(
  () => props.subject.deal_status ?? props.subject.pipedrive_deal_status ?? null
);
// `deal_status` é o enum cru da nota de sincronização do DEBTHUB (ver
// Debt::DebthubPhoneLookupService, `nota['status']`) — não é um enum do
// Chatwoot, e o DEBTHUB pode introduzir valor novo sem aviso. Mesmo padrão
// de `reasonLabel` em KanbanDebthubLinkPanel.vue: token conhecido traduz,
// token novo aparece cru em vez de quebrar a tela.
const dealStatusLabel = computed(() => {
  const value = dealStatus.value;
  if (!value) return null;
  const key = `KANBAN.DEBTHUB.DETAIL.SYNC_STATUS.${value}`;
  return te(key) ? t(key) : value;
});
const dealLastSyncedAt = computed(
  () =>
    props.subject.deal_last_synced_at ??
    props.subject.pipedrive_deal_last_synced_at ??
    null
);
const dealHasError = computed(
  () =>
    props.subject.deal_has_error ??
    props.subject.pipedrive_deal_has_error ??
    null
);
const pastaUrl = computed(() => props.subject.pasta_url ?? null);

const similarityPercent = computed(() => {
  const value = props.subject.similaridade;
  return typeof value === 'number' ? Math.round(value * 100) : null;
});

// Datas com HORA (timestamps do DEBTHUB/Pipedrive: `deal_last_synced_at`, `confirmed_at`,
// `add_time`/`update_time` do Pipedrive) contra datas SÓ-DIA (acordo, parcela,
// `expected_close_date`): formatos diferentes de propósito — 'Pp' numa data sem hora
// mostraria "00:00" para todo mundo, ruído que não existe na fonte.
const formatDate = value =>
  value ? format(new Date(value), 'Pp', { locale: dateFnsLocale.value }) : '';
const formatDateOnly = value =>
  value ? format(new Date(value), 'P', { locale: dateFnsLocale.value }) : '';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
// Aceita número E string numérica. As DUAS fontes chegam aqui com tipos
// diferentes, medido em produção em 2026-08-20: o DEBTHUB manda Float
// (`valor_acordo=1000.0`) e o Pipeboard manda String (`value="1000.5"`,
// porque `Debt::PipeboardClient#normalizar` faz `BigDecimal#to_s('F')` para
// não perder precisão atravessando JSON). Exigir `typeof === 'number'` fazia
// o VALOR DO NEGÓCIO sumir da tela inteira — a linha nem era renderizada.
// A suíte não pegava porque a fixture JS usava `value: 6000` (número),
// discordando do contrato que o spec Ruby exige.
const formatCurrency = value => {
  if (value === null || value === undefined || value === '') return null;
  const numero = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numero) ? currencyFormatter.format(numero) : null;
};

// Data só-dia vinda como string ISO do espelho ("2026-09-10"). `new Date` de
// uma ISO curta é interpretada em UTC, o que num fuso negativo volta o dia
// anterior; por isso a data é montada componente a componente.
const formatIsoDay = valor => {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor ?? ''));
  if (!partes) return null;
  const [, ano, mes, dia] = partes;
  return format(new Date(Number(ano), Number(mes) - 1, Number(dia)), 'P', {
    locale: dateFnsLocale.value,
  });
};

// Campo personalizado chega como texto cru do espelho. Sem isto a MESMA
// coluna mostrava "14569.76" ao lado de "R$ 1.000,50" e "2026-09-10" ao lado
// de "05/11/2025" — duas gramáticas de dinheiro e duas de data empilhadas.
const CAMPO_MOEDA = /valor|fipe|renda/;
const CAMPO_DATA = /data|vcto|vencimento/;
const formatarCampo = (chave, valor) => {
  if (CAMPO_MOEDA.test(chave)) return formatCurrency(valor) ?? valor;
  if (CAMPO_DATA.test(chave)) return formatIsoDay(valor) ?? valor;
  return valor;
};

const confirmedByLabel = computed(() => {
  const date = formatDate(props.subject.confirmed_at);
  const name = props.subject.confirmed_by?.name;
  return name
    ? t('KANBAN.DEBTHUB.LINKED_BY', { name, date })
    : t('KANBAN.DEBTHUB.LINKED_AT', { date });
});

// prefers-reduced-motion: mesmo composable e mesmo critério de
// KanbanColumn.vue — a entrada do painel não anima para quem pediu menos
// movimento.
const prefersReducedMotion = usePreferredReducedMotion();
const openAnimationClass = computed(() =>
  prefersReducedMotion.value === 'reduce' ? '' : 'animate-fade-in-up'
);

// -------------------------------------------------------------------------
// DOSSIE DEBTHUB (aba DEBTHUB) — Debt::DebthubPhoneLookupService#dossie
// -------------------------------------------------------------------------
const dossie = computed(() => props.subject.dossie ?? null);
const telefones = computed(() => dossie.value?.telefones ?? []);
const emails = computed(() => dossie.value?.emails ?? []);
const enderecos = computed(() => dossie.value?.enderecos ?? []);
const pastas = computed(() => dossie.value?.pastas ?? []);
const acordos = computed(() => dossie.value?.acordos ?? []);

const enderecoLinha = endereco => {
  const rua = [endereco.rua, endereco.numero].filter(Boolean).join(', ');
  const complemento = endereco.complemento ? ` - ${endereco.complemento}` : '';
  return `${rua}${complemento}`.trim();
};
const enderecoCidade = endereco =>
  [endereco.cidade, endereco.uf].filter(Boolean).join('/');

// Inferido do fluxo de atualização de status do DEBTHUB (acordo/serializers/acordo.py e
// acordo/serializers/acordo_update.py: acordo ATIVO zera parcela para '0' exceto a já '1',
// acordo não-ATIVO/não-Quitado marca as demais como '2') — NÃO é um enum documentado do
// lado de lá. Token fora do mapa aparece cru, nunca inventado.
const PARCELA_STATUS_KEY = { 0: 'PENDING', 1: 'PAID', 2: 'CANCELED' };
const parcelaStatusLabel = value => {
  const key = PARCELA_STATUS_KEY[value];
  return key ? t(`KANBAN.DEBTHUB.DETAIL.INSTALLMENT_STATUS.${key}`) : value;
};

const acordoStatusLabel = value => {
  if (!value) return null;
  const key = `KANBAN.DEBTHUB.DETAIL.AGREEMENT_STATUS_VALUE.${value}`;
  return te(key) ? t(key) : value;
};

// `activeTab` precisa existir ANTES do bloco de Pipedrive ao vivo (abaixo): o
// watcher de `pipedriveKey` lê `activeTab.value` para decidir se refaz a
// busca. O resto do estado/handlers de aba (`selectTab` etc.) vem depois,
// porque ele por sua vez depende de `fetchLivePipedrive`.
const TABS = ['debthub', 'pipedrive'];
const activeTab = ref('debthub');
const tabButtonEls = ref([]);

// -------------------------------------------------------------------------
// PIPEDRIVE AO VIVO (aba Pipedrive) — ver cabeçalho do arquivo. Definido
// ANTES de `selectTab` (abaixo) porque a troca de aba dispara a busca.
// -------------------------------------------------------------------------
// Ordem de preferência: o id do negócio é o mais específico; o do contato vem
// depois; e `pessoa:` é o caminho para quando o DEBTHUB não conhece NENHUM dos
// dois — o servidor resolve pelo documento do candidato. Medido na pasta
// 742031: `person_id` e `deal_id` nulos, e o negócio existindo no Pipedrive.
const pessoaId = computed(() => props.subject.pessoa_id ?? null);
const pipedriveKey = computed(() => {
  if (dealId.value) return `deal:${dealId.value}`;
  if (personId.value) return `person:${personId.value}`;
  if (pessoaId.value) return `pessoa:${pessoaId.value}`;
  return null;
});

const fetchedPipedriveKey = ref(null);
const isLoadingLivePipedrive = ref(false);
const livePipedriveUnavailable = ref(false);
const livePipedriveData = ref(null);
const livePipedriveDeepLink = ref(null);
const livePipedriveKind = computed(() => (dealId.value ? 'deal' : 'person'));

// Campos personalizados do Pipeboard. Lista vazia (e não nulo) para o template
// poder testar `.length` sem guarda extra.
const livePipedriveFields = computed(
  () => livePipedriveData.value?.campos ?? []
);
const livePipedriveDeals = computed(() => livePipedriveData.value?.deals ?? []);
const livePipedriveDealsTotal = computed(
  () => livePipedriveData.value?.deals_total ?? livePipedriveDeals.value.length
);
const livePipedriveDealsTruncated = computed(
  () => livePipedriveData.value?.deals_truncated === true
);

// Situação do negócio traduzida. O espelho guarda o valor do Pipedrive em
// inglês ("won"/"lost"/"open"/"deleted") e ele aparecia CRU na tela.
// Cor da pastilha por DESFECHO. `Ganho`, `Perdido` e `Em aberto` saíam todos
// em cinza, três desfechos com o mesmo tratamento — enquanto pastilhas
// decorativas da outra aba tinham cor. O texto já carrega o significado
// (1.4.1), então a cor aqui é reforço, não o único canal.
const COR_SITUACAO = {
  won: 'bg-n-teal-3 text-n-teal-12',
  lost: 'bg-n-ruby-3 text-n-ruby-12',
  open: 'bg-n-amber-3 text-n-amber-12',
  deleted: 'bg-n-slate-3 text-n-slate-12',
};
const corDaSituacao = valor =>
  COR_SITUACAO[String(valor ?? '').toLowerCase()] ??
  'bg-n-slate-3 text-n-slate-12';

const traduzirSituacao = valor => {
  const chave = String(valor ?? '').toLowerCase();
  const key = `KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_STATUS.${chave}`;
  return te(key) ? t(key) : valor;
};

// Rótulo do campo personalizado. Vem de um MAPA, não do nome da coluna: a
// derivação anterior (tirar `cf_`, trocar `_` por espaço) produzia
// "Telefone Contatos Primary Value" e "End 1 Formatted Address" na tela. O
// fallback humanizado continua para campo novo no CRM que ainda não tenha
// tradução — aparece legível, e não some.
const humanizarChave = chave =>
  chave
    .replace(/^cf_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/u, letra => letra.toUpperCase());

const rotuloDoCampo = chave => {
  const key = `KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_FIELD.${chave}`;
  return te(key) ? t(key) : humanizarChave(chave);
};

// UMA lista para a tela inteira: campo do núcleo e campo personalizado saem
// no mesmo formato {rotulo, valor}. Antes cada um tinha a sua estrofe própria
// de template, o que produzia a mistura de estilos que o painel mostrava.
// Valor vazio não entra — célula vazia não é informação.
const linha = (rotulo, valor) =>
  valor === null || valor === undefined || valor === ''
    ? null
    : { rotulo, valor: String(valor) };

const livePipedriveRows = computed(() => {
  const dado = livePipedriveData.value;
  if (!dado) return [];

  const rotulo = chave => t(`KANBAN.DEBTHUB.DETAIL.${chave}`);
  const nucleo =
    livePipedriveKind.value === 'deal'
      ? [
          linha(rotulo('FIELD_STATUS'), traduzirSituacao(dado.status)),
          linha(rotulo('FIELD_VALUE'), formatCurrency(dado.value)),
          linha(rotulo('FIELD_STAGE'), dado.stage_name),
          linha(rotulo('FIELD_PIPELINE'), dado.pipeline_name),
          linha(rotulo('FIELD_OWNER'), dado.owner_name),
          linha(rotulo('FIELD_PERSON'), dado.person_name),
          linha(rotulo('FIELD_LOST_REASON'), dado.lost_reason),
          linha(rotulo('FIELD_CREATED'), formatDateOnly(dado.add_time)),
          linha(rotulo('FIELD_UPDATED'), formatDateOnly(dado.update_time)),
        ]
      : [
          linha(rotulo('FIELD_OWNER'), dado.owner_name),
          linha(rotulo('FIELD_ORG'), dado.org_name),
        ];

  return [
    ...nucleo,
    ...livePipedriveFields.value
      // `..._moeda` não vira linha: "BRL" sozinho é nome de coluna vazando
      // para a tela, e o símbolo já vem no valor formatado ao lado.
      .filter(campo => !campo.chave.endsWith('_moeda'))
      .map(campo =>
        linha(
          rotuloDoCampo(campo.chave),
          formatarCampo(campo.chave, campo.valor)
        )
      ),
  ].filter(Boolean);
});

// Camada BASE: o que o espelho do DEBTHUB já sabia, no MESMO formato das
// demais linhas. Antes cada item tinha markup próprio, o que produzia a
// mistura de tamanhos e estilos visível no painel.
const basePipedriveRows = computed(() =>
  [
    linha(t('KANBAN.DEBTHUB.DETAIL.FIELD_PERSON_ID'), personId.value),
    linha(t('KANBAN.DEBTHUB.DETAIL.FIELD_DEAL_ID'), dealId.value),
    linha(t('KANBAN.DEBTHUB.DETAIL.FIELD_SYNC_STATUS'), dealStatusLabel.value),
    linha(
      t('KANBAN.DEBTHUB.DETAIL.FIELD_SYNCED_AT'),
      dealLastSyncedAt.value ? formatDate(dealLastSyncedAt.value) : null
    ),
  ].filter(Boolean)
);

// Título da seção: o negócio tem título próprio; o contato usa o nome.
const livePipedriveTitle = computed(
  () => livePipedriveData.value?.title || livePipedriveData.value?.name || ''
);

// "Não há negócio" só é verdade quando NEM o espelho do DEBTHUB NEM a consulta
// ao Pipeboard acharam um. Antes a mensagem saía sempre que o DEBTHUB não
// tinha `deal_id`, mesmo com o negócio existindo e listado logo abaixo.
const semNegocioAlgum = computed(
  () =>
    !dealId.value &&
    !dealUrl.value &&
    livePipedriveDeals.value.length === 0 &&
    livePipedriveKind.value !== 'deal'
);

// O link profundo que o backend devolve aponta para o recurso CONSULTADO (a
// pessoa). Cada negócio da lista tem id próprio, então a URL dele é derivada do
// mesmo domínio — trocando o sufixo, nunca concatenando entrada do usuário.
const dealDeepLink = id => {
  const base = livePipedriveDeepLink.value;
  if (!base) return null;
  return base.replace(/\/(?:deal|person)\/\d+\/?$/, `/deal/${id}`);
};

const fetchLivePipedrive = async () => {
  const key = pipedriveKey.value;
  if (!key || !props.conversationDisplayId) return;
  if (fetchedPipedriveKey.value === key) return; // já buscado (ou em voo) para este id.

  fetchedPipedriveKey.value = key;
  isLoadingLivePipedrive.value = true;
  livePipedriveUnavailable.value = false;
  try {
    const { data } = await DebtConversationApi.pipedriveLookup(
      props.conversationDisplayId,
      // Um só, na mesma ordem de precedência de `pipedriveKey`.
      // eslint-disable-next-line no-nested-ternary
      dealId.value
        ? { dealId: dealId.value }
        : personId.value
          ? { personId: personId.value }
          : { pessoaId: pessoaId.value }
    );
    livePipedriveData.value = data.available
      ? (data.deal ?? data.person)
      : null;
    livePipedriveUnavailable.value = !data.available;
    livePipedriveDeepLink.value = data.deep_link ?? null;
  } catch (error) {
    livePipedriveUnavailable.value = true;
  } finally {
    isLoadingLivePipedrive.value = false;
  }
};

// O assunto exibido pode TROCAR sem o painel fechar (o diálogo-pai reaproveita a mesma
// instância — ver KanbanConversationModal#openDetail). Um id novo invalida o retrato do id
// anterior; sem isto, trocar de candidato com a aba Pipedrive já aberta mostraria o negócio
// de OUTRO candidato por um instante (ou para sempre, se a chamada nova falhar).
watch(pipedriveKey, () => {
  livePipedriveData.value = null;
  livePipedriveUnavailable.value = false;
  livePipedriveDeepLink.value = null;
  fetchedPipedriveKey.value = null;
  if (activeTab.value === 'pipedrive') fetchLivePipedrive();
});

const liveOpenLink = computed(
  () => dealUrl.value || personUrl.value || livePipedriveDeepLink.value
);

// -------------------------------------------------------------------------
// ABAS — `TABS`/`activeTab`/`tabButtonEls` já declarados acima (ver comentário).
// -------------------------------------------------------------------------
const tabId = key => `kanban-debthub-detail-tab-${key}`;
const panelId = key => `kanban-debthub-detail-panel-${key}`;

const selectTab = key => {
  activeTab.value = key;
  if (key === 'pipedrive') fetchLivePipedrive();
};

// Padrão WAI-ARIA de abas com ativação automática: as setas movem seleção E
// foco juntos, sem exigir Enter/Espaço para confirmar a troca.
const onTabKeydown = event => {
  if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
  event.preventDefault();
  const currentIndex = TABS.indexOf(activeTab.value);
  const delta = event.key === 'ArrowRight' ? 1 : -1;
  const nextIndex = (currentIndex + delta + TABS.length) % TABS.length;
  selectTab(TABS[nextIndex]);
  nextTick(() => tabButtonEls.value[nextIndex]?.focus());
};

const setTabButtonEl = (el, index) => {
  if (el) tabButtonEls.value[index] = el;
};
</script>

<template>
  <div
    id="kanban-debthub-detail-panel"
    data-testid="kanban-debthub-detail-panel"
    role="region"
    :aria-label="t('KANBAN.DEBTHUB.DETAIL.TITLE')"
    class="flex flex-col min-h-0"
    :class="openAnimationClass"
  >
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0 border-n-weak"
    >
      <h3 class="m-0 text-xs font-semibold uppercase text-n-slate-11">
        {{ t('KANBAN.DEBTHUB.DETAIL.TITLE') }}
      </h3>
      <NextButton
        slate
        ghost
        xs
        icon="i-lucide-x"
        data-testid="kanban-debthub-detail-close"
        :aria-label="t('KANBAN.DEBTHUB.DETAIL.CLOSE')"
        @click="close"
      />
    </div>

    <div
      role="tablist"
      :aria-label="t('KANBAN.DEBTHUB.DETAIL.TABS_LABEL')"
      class="flex px-1 border-b shrink-0 border-n-weak"
    >
      <button
        v-for="(tabKey, index) in TABS"
        :id="tabId(tabKey)"
        :key="tabKey"
        :ref="el => setTabButtonEl(el, index)"
        type="button"
        role="tab"
        :data-testid="`kanban-debthub-detail-tab-${tabKey}`"
        :aria-selected="activeTab === tabKey ? 'true' : 'false'"
        :aria-controls="panelId(tabKey)"
        :tabindex="activeTab === tabKey ? 0 : -1"
        class="px-3 py-2 text-xs rounded-t-lg outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
        :class="
          activeTab === tabKey
            ? 'font-semibold text-n-slate-12 bg-n-alpha-2'
            : 'text-n-slate-11'
        "
        @click="selectTab(tabKey)"
        @keydown="onTabKeydown"
      >
        {{ t(`KANBAN.DEBTHUB.DETAIL.TAB_${tabKey.toUpperCase()}`) }}
      </button>
    </div>

    <!-- Cada aba rola de forma independente da OUTRA e do restante do
         diálogo: `overflow-y-auto` isolado por painel, e não um único
         contêiner compartilhado. -->
    <div
      v-show="activeTab === 'debthub'"
      :id="panelId('debthub')"
      role="tabpanel"
      :aria-labelledby="tabId('debthub')"
      data-testid="kanban-debthub-detail-panel-debthub"
      class="flex flex-col flex-1 min-h-0 gap-4 p-3 overflow-y-auto"
    >
      <!-- Identificação -->
      <div class="flex flex-col gap-1">
        <p class="m-0 text-sm font-semibold tabular-nums text-n-slate-12">
          <template v-if="subject.cpf">
            {{ t('KANBAN.DEBTHUB.CPF_LABEL') }} {{ subject.cpf }}
          </template>
          <template v-else>{{ t('KANBAN.DEBTHUB.NO_CPF') }}</template>
        </p>
        <p v-if="subject.nome" class="m-0 text-xs text-n-slate-11">
          {{ subject.nome }}
        </p>
        <p
          v-if="kind === 'proposed' && similarityPercent !== null"
          class="m-0 text-xs text-n-slate-11"
        >
          {{
            t('KANBAN.DEBTHUB.DETAIL.SIMILARITY', {
              percent: similarityPercent,
            })
          }}
        </p>
        <p v-if="kind === 'confirmed'" class="m-0 text-xs text-n-slate-11">
          {{ confirmedByLabel }}
        </p>
        <a
          v-if="pastaUrl"
          :href="pastaUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="self-start inline-flex items-center min-h-[24px] py-1 text-xs underline text-n-blue-11"
        >
          {{ t('KANBAN.DEBTHUB.OPEN_PASTA') }}
        </a>
        <p
          v-else
          data-testid="kanban-debthub-detail-no-pasta"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_PASTA') }}
        </p>
      </div>

      <!-- Telefones -->
      <div class="flex flex-col gap-1.5">
        <h4 class="m-0 text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_PHONES') }}
        </h4>
        <p
          v-if="telefones.length === 0"
          data-testid="kanban-debthub-detail-no-phones"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_PHONES') }}
        </p>
        <ul v-else class="flex flex-col gap-1.5 m-0 p-0 list-none">
          <li
            v-for="telefone in telefones"
            :key="telefone.id"
            data-testid="kanban-debthub-detail-phone"
            class="p-2 text-xs rounded-lg bg-n-alpha-1"
          >
            <p
              class="flex flex-wrap items-center gap-1 m-0 font-medium tabular-nums text-n-slate-12"
            >
              {{ telefone.numero }}
              <!-- Estado nunca só por cor (1.4.1): o rótulo textual já carrega o
                   significado; a pastilha é reforço visual. -->
              <span
                v-if="telefone.match"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-n-teal-3 text-n-teal-12"
              >
                {{ t('KANBAN.DEBTHUB.DETAIL.PHONE_MATCH') }}
              </span>
              <span
                v-if="telefone.hot"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-n-amber-3 text-n-amber-12"
              >
                {{ t('KANBAN.DEBTHUB.DETAIL.PHONE_HOT') }}
              </span>
              <span
                v-if="telefone.wpp_hot"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-n-teal-3 text-n-teal-12"
              >
                {{ t('KANBAN.DEBTHUB.DETAIL.PHONE_WPP_HOT') }}
              </span>
            </p>
            <p
              v-if="typeof telefone.similaridade === 'number'"
              class="m-0 mt-0.5 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.PHONE_SIMILARITY', {
                  percent: Math.round(telefone.similaridade * 100),
                })
              }}
            </p>
            <p
              v-if="telefone.pessoa_relacionada"
              class="m-0 mt-0.5 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.PHONE_RELATED', {
                  name: telefone.pessoa_relacionada,
                })
              }}
            </p>
          </li>
        </ul>
      </div>

      <!-- E-mails -->
      <div class="flex flex-col gap-1.5">
        <h4 class="m-0 text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_EMAILS') }}
        </h4>
        <p
          v-if="emails.length === 0"
          data-testid="kanban-debthub-detail-no-emails"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_EMAILS') }}
        </p>
        <ul v-else class="flex flex-col gap-1 m-0 p-0 list-none">
          <li
            v-for="email in emails"
            :key="email.id"
            data-testid="kanban-debthub-detail-email"
            class="text-xs break-words text-n-slate-12"
          >
            {{ email.email }}
          </li>
        </ul>
      </div>

      <!-- Endereços -->
      <div class="flex flex-col gap-1.5">
        <h4 class="m-0 text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_ADDRESSES') }}
        </h4>
        <p
          v-if="enderecos.length === 0"
          data-testid="kanban-debthub-detail-no-addresses"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_ADDRESSES') }}
        </p>
        <ul v-else class="flex flex-col gap-1.5 m-0 p-0 list-none">
          <li
            v-for="endereco in enderecos"
            :key="endereco.id"
            data-testid="kanban-debthub-detail-address"
            class="p-2 text-xs rounded-lg bg-n-alpha-1"
          >
            <p class="m-0 text-n-slate-12">{{ enderecoLinha(endereco) }}</p>
            <p v-if="endereco.bairro" class="m-0 mt-0.5 text-n-slate-11">
              {{ endereco.bairro }}
            </p>
            <p class="m-0 mt-0.5 text-n-slate-11">
              {{ enderecoCidade(endereco) }}
              <span v-if="endereco.cep"> · {{ endereco.cep }}</span>
            </p>
            <p v-if="endereco.origem" class="m-0 mt-0.5 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.ADDRESS_ORIGIN', {
                  origin: endereco.origem,
                })
              }}
            </p>
          </li>
        </ul>
      </div>

      <!-- Pastas -->
      <div class="flex flex-col gap-1.5">
        <h4 class="m-0 text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_FOLDERS') }}
        </h4>
        <p
          v-if="pastas.length === 0"
          data-testid="kanban-debthub-detail-no-folders"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_FOLDERS') }}
        </p>
        <ul v-else class="flex flex-col gap-1.5 m-0 p-0 list-none">
          <li
            v-for="pasta in pastas"
            :key="pasta.id"
            data-testid="kanban-debthub-detail-folder"
            class="p-2 text-xs rounded-lg bg-n-alpha-1"
          >
            <p class="m-0 font-medium text-n-slate-12">
              {{ pasta.pasta || pasta.id }}
            </p>
            <p v-if="pasta.carteira" class="m-0 mt-0.5 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.FOLDER_CARRIER', {
                  carteira: pasta.carteira,
                })
              }}
            </p>
            <p v-if="pasta.num_sinistro" class="m-0 mt-0.5 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.FOLDER_CLAIM', {
                  numero: pasta.num_sinistro,
                })
              }}
            </p>
            <p v-if="pasta.seguradora?.nome" class="m-0 mt-0.5 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.FOLDER_INSURER', {
                  nome: pasta.seguradora.nome,
                })
              }}
            </p>
          </li>
        </ul>
      </div>

      <!-- Acordos + parcelas -->
      <div class="flex flex-col gap-1.5">
        <h4 class="m-0 text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_AGREEMENTS') }}
        </h4>
        <p
          v-if="acordos.length === 0"
          data-testid="kanban-debthub-detail-no-agreements"
          class="m-0 text-xs text-n-slate-11"
        >
          {{ t('KANBAN.DEBTHUB.DETAIL.NO_AGREEMENTS') }}
        </p>
        <ul v-else class="flex flex-col gap-2 m-0 p-0 list-none">
          <li
            v-for="acordo in acordos"
            :key="acordo.id"
            data-testid="kanban-debthub-detail-agreement"
            class="flex flex-col gap-1 p-2 text-xs rounded-lg bg-n-alpha-1"
          >
            <p class="m-0 font-medium text-n-slate-12">
              {{ acordo.nome || t('KANBAN.DEBTHUB.DETAIL.SECTION_AGREEMENTS') }}
              <span
                v-if="acordoStatusLabel(acordo.status_acordo)"
                class="text-n-slate-11"
              >
                · {{ acordoStatusLabel(acordo.status_acordo) }}
              </span>
            </p>
            <p
              v-if="formatCurrency(acordo.valor_acordo)"
              class="m-0 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_VALUE', {
                  value: formatCurrency(acordo.valor_acordo),
                })
              }}
            </p>
            <p
              v-if="formatCurrency(acordo.valor_original_da_divida)"
              class="m-0 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_ORIGINAL_DEBT', {
                  value: formatCurrency(acordo.valor_original_da_divida),
                })
              }}
            </p>
            <p
              v-if="typeof acordo.percentual_de_desconto === 'number'"
              class="m-0 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_DISCOUNT', {
                  percent: Math.round(acordo.percentual_de_desconto),
                })
              }}
            </p>
            <p
              v-if="formatCurrency(acordo.recebido)"
              class="m-0 text-n-slate-11"
            >
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_RECEIVED', {
                  value: formatCurrency(acordo.recebido),
                })
              }}
            </p>
            <p v-if="acordo.data_acordo" class="m-0 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_DATE', {
                  date: formatDateOnly(acordo.data_acordo),
                })
              }}
            </p>
            <p v-if="acordo.data_ganho" class="m-0 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_WON_AT', {
                  date: formatDateOnly(acordo.data_ganho),
                })
              }}
            </p>
            <p v-if="acordo.data_quebra" class="m-0 text-n-slate-11">
              {{
                t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_BROKEN_AT', {
                  date: formatDateOnly(acordo.data_quebra),
                })
              }}
            </p>
            <p
              v-if="
                acordo.operador_nome ||
                acordo.sdr_nome ||
                acordo.backoffice_nome
              "
              class="m-0 text-n-slate-11"
            >
              <span v-if="acordo.operador_nome">
                {{
                  t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_OPERATOR', {
                    name: acordo.operador_nome,
                  })
                }}
              </span>
              <span v-if="acordo.sdr_nome">
                ·
                {{
                  t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_SDR', {
                    name: acordo.sdr_nome,
                  })
                }}
              </span>
              <span v-if="acordo.backoffice_nome">
                ·
                {{
                  t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_BACKOFFICE', {
                    name: acordo.backoffice_nome,
                  })
                }}
              </span>
            </p>

            <!-- Parcelas: rolagem própria e teto (ver
                 Debt::DebthubPhoneLookupService::ACORDO_PARCELAS_LIMIT) — uma
                 lista de dezenas de parcelas não pode estourar a aba. -->
            <template v-if="acordo.parcelas?.itens?.length">
              <p
                class="m-0 mt-1 text-[11px] font-semibold uppercase text-n-slate-11"
              >
                {{ t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_INSTALLMENTS') }}
              </p>
              <!-- O `role="group"` (que torna a região rolável alcançável por
                   teclado) fica no WRAPPER, e não no `<ul>`: posto na lista, ele
                   SUBSTITUI o papel implícito `list` e os `<li>` de parcela
                   ficam órfãos — `axe` acusa 8 nós em `listitem`, impacto
                   serious, e o leitor de tela deixa de anunciar "lista, N
                   itens". Verificado por renderização em 2026-08-20. -->
              <div
                tabindex="0"
                role="group"
                :aria-label="t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_INSTALLMENTS')"
                class="overflow-y-auto rounded max-h-32 bg-n-solid-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              >
                <ul
                  data-testid="kanban-debthub-detail-installments"
                  class="flex flex-col gap-1 p-1 m-0 list-none"
                >
                  <li
                    v-for="parcela in acordo.parcelas.itens"
                    :key="parcela.num_parcela"
                    class="flex items-center justify-between gap-2 px-1"
                  >
                    <span class="tabular-nums text-n-slate-12">
                      #{{ parcela.num_parcela }} ·
                      {{ formatDateOnly(parcela.data_vencimento) }}
                    </span>
                    <span class="tabular-nums text-n-slate-11">
                      {{ formatCurrency(parcela.valor_parcela) }} ·
                      {{ parcelaStatusLabel(parcela.status_parcela) }}
                    </span>
                  </li>
                </ul>
              </div>
              <p
                v-if="acordo.parcelas.truncado"
                data-testid="kanban-debthub-detail-installments-truncated"
                class="m-0 text-n-slate-11"
              >
                {{
                  t('KANBAN.DEBTHUB.DETAIL.AGREEMENT_INSTALLMENTS_TRUNCATED', {
                    shown: acordo.parcelas.itens.length,
                    total: acordo.parcelas.total,
                  })
                }}
              </p>
            </template>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-show="activeTab === 'pipedrive'"
      :id="panelId('pipedrive')"
      role="tabpanel"
      :aria-labelledby="tabId('pipedrive')"
      data-testid="kanban-debthub-detail-panel-pipedrive"
      class="flex flex-col flex-1 min-h-0 gap-3 p-3 overflow-y-auto"
    >
      <!-- ESTADO DA CONSULTA primeiro: carregando / indisponível / conteúdo.
           Os três são exclusivos, então nunca há duas mensagens na tela. -->
      <p
        v-if="isLoadingLivePipedrive"
        role="status"
        data-testid="kanban-debthub-detail-pipedrive-loading"
        class="m-0 text-xs text-n-slate-11"
      >
        {{ t('KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_LOADING') }}
      </p>
      <p
        v-if="!isLoadingLivePipedrive && livePipedriveUnavailable"
        role="status"
        data-testid="kanban-debthub-detail-pipedrive-unavailable"
        class="m-0 flex items-start gap-1.5 text-xs text-n-amber-11"
      >
        <span
          aria-hidden="true"
          class="i-lucide-triangle-alert size-3.5 shrink-0 mt-px"
        />
        <span class="min-w-0">{{
          t('KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_UNAVAILABLE')
        }}</span>
      </p>

      <!-- Conteúdo AO VIVO. Separado das seções abaixo de propósito: a
           consulta ao Pipeboard pode falhar, e quando falha a tela continua
           mostrando o que o DEBTHUB já sabia — o aviso acima é ADICIONAL, não
           substitui a camada base. Era o que o `v-else` anterior quebrava. -->
      <div
        v-if="livePipedriveData && !isLoadingLivePipedrive"
        data-testid="kanban-debthub-detail-pipedrive-live"
        class="flex flex-col gap-3"
      >
        <!-- Título do recurso consultado. `break-words` porque título de
             negócio traz nome próprio comprido e o painel tem 320px. -->
        <h4
          v-if="livePipedriveTitle"
          class="m-0 text-sm font-semibold leading-snug break-words text-n-slate-12"
        >
          {{ livePipedriveTitle }}
        </h4>

        <!-- LISTA ÚNICA, empilhada. O rótulo vai ACIMA do valor, e não ao
             lado: a versão anterior usava `grid-cols-[minmax(0,auto)_1fr]`,
             e `auto` deixava o rótulo mais comprido ditar a largura da
             coluna — num painel de 320px o valor sobrava com ~4 caracteres e
             quebrava uma letra por linha. Empilhado, o valor tem sempre a
             largura inteira, em qualquer viewport. -->
        <dl
          v-if="livePipedriveRows.length"
          data-testid="kanban-debthub-detail-pipedrive-fields"
          class="flex flex-col gap-2.5 m-0"
        >
          <div
            v-for="(item, indice) in livePipedriveRows"
            :key="`${item.rotulo}-${indice}`"
            class="flex flex-col min-w-0 gap-0.5 sm:grid sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-x-3 sm:gap-y-0 lg:flex lg:flex-col lg:gap-0.5"
          >
            <dt
              class="m-0 text-[11px] font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ item.rotulo }}
            </dt>
            <dd class="m-0 text-sm break-words text-n-slate-12">
              {{ item.valor }}
            </dd>
          </div>
        </dl>

        <!-- NEGÓCIOS DA PESSOA. É o caso que motivou trocar a fonte: o
             espelho do DEBTHUB devolve `deal_id` nulo para parte das pastas,
             e o negócio só aparece pelo person_id. -->
        <section
          v-if="livePipedriveDeals.length"
          data-testid="kanban-debthub-detail-pipedrive-deals"
          class="flex flex-col gap-1.5 pt-3 border-t border-n-weak"
        >
          <h4 class="m-0 text-xs font-semibold text-n-slate-12">
            {{ t('KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_DEALS') }}
          </h4>
          <a
            v-for="negocio in livePipedriveDeals"
            :key="negocio.id"
            :href="dealDeepLink(negocio.id)"
            target="_blank"
            rel="noopener noreferrer"
            class="flex flex-col min-w-0 gap-1 px-2 py-1.5 no-underline rounded-md bg-n-alpha-1 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          >
            <span
              class="text-xs font-medium break-words text-n-slate-12 line-clamp-2"
            >
              {{ negocio.title }}
            </span>
            <span class="flex flex-wrap items-center gap-1.5">
              <span
                class="px-1.5 py-0.5 text-[11px] font-medium rounded"
                :class="corDaSituacao(negocio.status)"
              >
                {{ traduzirSituacao(negocio.status) }}
              </span>
              <span
                v-if="formatCurrency(negocio.value)"
                class="text-[11px] tabular-nums text-n-slate-11"
              >
                {{ formatCurrency(negocio.value) }}
              </span>
              <span
                v-if="negocio.stage_name"
                class="text-[11px] text-n-slate-11"
              >
                {{ negocio.stage_name }}
              </span>
            </span>
          </a>
          <!-- Corte declarado, nunca silencioso: com o teto batido, a lista
               acima não é "todos os negócios desta pessoa". -->
          <p
            v-if="livePipedriveDealsTruncated"
            data-testid="kanban-debthub-detail-pipedrive-deals-truncated"
            class="m-0 text-[11px] text-n-slate-11"
          >
            {{
              t('KANBAN.DEBTHUB.DETAIL.PIPEDRIVE_DEALS_TRUNCATED', {
                shown: livePipedriveDeals.length,
                total: livePipedriveDealsTotal,
              })
            }}
          </p>
        </section>
      </div>

      <p
        v-if="semNegocioAlgum"
        data-testid="kanban-debthub-detail-no-deal"
        class="m-0 text-xs text-n-slate-11"
      >
        {{ t('KANBAN.DEBTHUB.DETAIL.NO_DEAL') }}
      </p>
      <p
        v-if="!personId && !personUrl"
        data-testid="kanban-debthub-detail-no-person"
        class="m-0 text-xs text-n-slate-11"
      >
        {{ t('KANBAN.DEBTHUB.DETAIL.NO_PERSON') }}
      </p>

      <!-- O QUE O DEBTHUB JÁ SABIA, no mesmo formato do resto. Fica por
             último porque é metadado de sincronização, não o caso. -->
      <section
        v-if="basePipedriveRows.length"
        class="flex flex-col gap-2 pt-3 border-t border-n-weak"
      >
        <h4 class="m-0 text-xs font-semibold text-n-slate-12">
          {{ t('KANBAN.DEBTHUB.DETAIL.SECTION_SYNC') }}
        </h4>
        <dl class="flex flex-col gap-2 m-0">
          <div
            v-for="(item, indice) in basePipedriveRows"
            :key="`base-${item.rotulo}-${indice}`"
            class="flex flex-col min-w-0 gap-0.5 sm:grid sm:grid-cols-[7.5rem_minmax(0,1fr)] sm:gap-x-3 sm:gap-y-0 lg:flex lg:flex-col lg:gap-0.5"
          >
            <dt
              class="m-0 text-[11px] font-medium tracking-wide uppercase text-n-slate-11"
            >
              {{ item.rotulo }}
            </dt>
            <dd class="m-0 text-sm break-words text-n-slate-12 tabular-nums">
              {{ item.valor }}
            </dd>
          </div>
        </dl>
        <!-- Estado nunca só por cor (1.4.1): o ícone e o texto carregam o
               aviso, a cor ruby é reforço. Sem `role="alert"`: é conteúdo
               PERMANENTE dentro de um tabpanel que nasce `display:none`, não
               um evento que ACONTECEU. -->
        <p
          v-if="dealHasError"
          data-testid="kanban-debthub-detail-deal-sync-error"
          class="m-0 flex items-start gap-1.5 text-xs text-n-ruby-11"
        >
          <span
            aria-hidden="true"
            class="i-lucide-triangle-alert size-3.5 shrink-0 mt-px"
          />
          <span class="min-w-0">{{
            t('KANBAN.DEBTHUB.DETAIL.DEAL_SYNC_ERROR')
          }}</span>
        </p>
      </section>

      <!-- AÇÃO no rodapé, e uma só: o link de abrir sai de perto do dado e
             vira destino, não ruído no meio da leitura. -->
      <a
        v-if="liveOpenLink"
        :href="liveOpenLink"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center justify-center gap-1.5 px-2 py-1.5 mt-auto text-xs font-medium no-underline border rounded-md border-n-weak text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      >
        <span aria-hidden="true" class="i-lucide-external-link size-3.5" />
        {{ t('KANBAN.DEBTHUB.DETAIL.OPEN_IN_PIPEDRIVE') }}
      </a>
    </div>
  </div>
</template>
